import { useEffect, useMemo, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Image as ImageIcon, DownloadSimple } from "@phosphor-icons/react";

const EMPTY = {
  grsr_no: "", grsr_date: "", retailer_id: "", company_id: "", salesman_name: "",
  mode_of_transport: "", builty_no: "", total_shipper: "",
  material_booked_by: "", retailer_doc_no: "", retailer_doc_date: "",
  document_quantity: "", actual_quantity_received: "",
  quickbill_no: "", quickbill_cn_date: "", shopper_cn_no: "", shopper_cn_date: "",
  remark: "", photo_data: "",
  saleable_with_box: "", saleable_without_box: "",
  non_moving: "", defective: "",
  saleable_without_box_pb_tag_sticker: "", damaged: "",
};

const CATEGORY_FIELDS = [
  ["saleable_with_box",                    "Saleable — With Box"],
  ["saleable_without_box",                 "Saleable — Without Box"],
  ["non_moving",                           "Non Moving"],
  ["defective",                            "Defective"],
  ["saleable_without_box_pb_tag_sticker",  "Saleable Without Box · PB Tag Sticker"],
  ["damaged",                              "Damaged"],
];

// Records up to & including this ISO date go to Old GRSR.
const OLD_CUTOFF = new Date("2026-07-31T23:59:59");

// Parse a grsr_date string into a Date. Handles yyyy-mm-dd, dd-mm-yyyy, dd/mm/yyyy.
function parseGrsrDate(s) {
  if (!s) return null;
  const t = String(s).trim();
  let m;
  if ((m = t.match(/^(\d{4})-(\d{2})-(\d{2})/))) return new Date(+m[1], +m[2]-1, +m[3]);
  if ((m = t.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/))) return new Date(+m[3], +m[2]-1, +m[1]);
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

// Display any date format as dd-mm-yyyy for the table.
function fmtDMY(s) {
  const d = parseGrsrDate(s);
  if (!d) return s || "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const CSV_COLS = [
  "grsr_no","grsr_date","retailer_name","company_name","salesman_name","actual_quantity_received",
  "document_quantity","total_shipper","mode_of_transport","builty_no",
  "material_booked_by","retailer_doc_no","retailer_doc_date",
  "quickbill_no","quickbill_cn_date","shopper_cn_no","shopper_cn_date",
  "saleable_with_box","saleable_without_box","non_moving","defective",
  "saleable_without_box_pb_tag_sticker","damaged","remark","entered_by","created_at",
];

function toCsvValue(v) {
  const s = String(v == null ? "" : v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function downloadRowsCsv(rows, filename) {
  const lines = [CSV_COLS.join(",")];
  for (const r of rows) {
    lines.push(CSV_COLS.map(c => toCsvValue(r[c])).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function GRSR() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [photoView, setPhotoView] = useState(null);
  const [tab, setTab] = useState("current"); // "current" | "old"
  const [q, setQ] = useState("");
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data }, { data: r }, { data: c }, { data: s }] = await Promise.all([
      client.get("/grsr"), client.get("/retailers"), client.get("/companies"), client.get("/salesmen"),
    ]);
    setRows(data); setRetailers(r); setCompanies(c); setSalesmen(s);
  };
  useEffect(() => { load(); }, []);

  const { currentRows, oldRows } = useMemo(() => {
    const c = [], o = [];
    for (const r of rows) {
      const d = parseGrsrDate(r.grsr_date);
      if (d && d <= OLD_CUTOFF) o.push(r);
      else c.push(r); // rows with unparsable/missing dates fall into current
    }
    // Sort each bucket: pending (no Shopper CN) first; then descending by grsr_date
    const cmp = (a, b) => {
      const aPending = !((a.shopper_cn_no || "").toString().trim());
      const bPending = !((b.shopper_cn_no || "").toString().trim());
      if (aPending !== bPending) return aPending ? -1 : 1;
      const da = parseGrsrDate(a.grsr_date);
      const db = parseGrsrDate(b.grsr_date);
      if (da && db) return db - da;
      if (da && !db) return -1;
      if (!da && db) return 1;
      return 0;
    };
    c.sort(cmp); o.sort(cmp);
    return { currentRows: c, oldRows: o };
  }, [rows]);

  const visible = useMemo(() => {
    const base = tab === "old" ? oldRows : currentRows;
    const s = q.trim().toLowerCase();
    if (!s) return base;
    return base.filter(r =>
      (r.grsr_no || "").toLowerCase().includes(s) ||
      (r.retailer_name || "").toLowerCase().includes(s) ||
      (r.company_name || "").toLowerCase().includes(s) ||
      (r.salesman_name || "").toLowerCase().includes(s)
    );
  }, [tab, oldRows, currentRows, q]);

  // Reset search when switching tabs so results feel predictable
  useEffect(() => { setQ(""); }, [tab]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    if (!isAdmin) return;
    setForm({ ...EMPTY, ...r });
    setEditId(r.id); setOpen(true);
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    setForm({ ...form, photo_data: await fileToBase64(f) });
  };

  const num = (v) => v === "" || v === undefined || v === null ? 0 : Number(v);

  const save = async () => {
    if (!form.grsr_no.trim() || !form.grsr_date || !form.retailer_id || !form.salesman_name)
      return toast.error("GRSR No, Date, Retailer and Salesman are required");
    if (form.actual_quantity_received === "" || form.actual_quantity_received === null)
      return toast.error("Actual Quantity Received is required");
    try {
      const p = {
        ...form,
        total_shipper: num(form.total_shipper),
        document_quantity: num(form.document_quantity),
        actual_quantity_received: num(form.actual_quantity_received),
        saleable_with_box: num(form.saleable_with_box),
        saleable_without_box: num(form.saleable_without_box),
        non_moving: num(form.non_moving),
        defective: num(form.defective),
        saleable_without_box_pb_tag_sticker: num(form.saleable_without_box_pb_tag_sticker),
        damaged: num(form.damaged),
      };
      if (editId) await client.put(`/grsr/${editId}`, p); else await client.post("/grsr", p);
      toast.success(editId ? "Updated" : "GRSR recorded"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this GRSR?")) return;
    try { await client.delete(`/grsr/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const F = (label, key, type = "text", required = false) => (
    <div>
      <label className="label-cap">{label}{required && " *"}</label>
      <Input data-testid={`grsr-${key}-input`} type={type} value={form[key]} onChange={(e)=>setForm({...form, [key]: e.target.value})}/>
    </div>
  );

  const doExport = () => {
    if (!visible.length) return toast.error("Nothing to export");
    const name = tab === "old"
      ? `grsr-old-up-to-2026-07-31-${new Date().toISOString().slice(0,10)}.csv`
      : `grsr-current-from-2026-08-01-${new Date().toISOString().slice(0,10)}.csv`;
    downloadRowsCsv(visible, name);
  };

  return (
    <div>
      <PageHeader title="GRSR" subtitle="Goods Received Sales Return"
        actions={<Button data-testid="grsr-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> New GRSR</Button>}
      />
      <Section>
        {/* Tab strip + export */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="inline-flex bg-white border border-slate-200 rounded-sm overflow-hidden" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "current"}
              data-testid="grsr-tab-current"
              onClick={()=>setTab("current")}
              className={`px-4 py-2 text-sm font-semibold border-r border-slate-200 ${tab==="current" ? "bg-orange-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              GRSR <span className="ml-1.5 text-xs opacity-75">({currentRows.length})</span>
              <div className="text-[10px] font-normal opacity-80">From 01-08-2026</div>
            </button>
            <button
              role="tab"
              aria-selected={tab === "old"}
              data-testid="grsr-tab-old"
              onClick={()=>setTab("old")}
              className={`px-4 py-2 text-sm font-semibold ${tab==="old" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              Old GRSR <span className="ml-1.5 text-xs opacity-75">({oldRows.length})</span>
              <div className="text-[10px] font-normal opacity-80">Up to 31-07-2026</div>
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Input
                data-testid="grsr-search-input"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                placeholder="Search GRSR no, retailer, salesman…"
                className="rounded-sm w-64 pr-7"
              />
              {q && (
                <button
                  data-testid="grsr-search-clear"
                  onClick={()=>setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"
                  title="Clear"
                >×</button>
              )}
            </div>
            <Button
              variant="outline"
              className="rounded-sm"
              data-testid={`grsr-export-${tab}-btn`}
              onClick={doExport}
              disabled={!visible.length}
            >
              <DownloadSimple size={14} className="mr-1"/> Download CSV
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">GRSR No</th>
              <th className="px-4 py-3">Quickbill No</th>
              <th className="px-4 py-3">Shopper CN No</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Retailer</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Salesman</th>
              <th className="px-4 py-3">Actual Qty</th>
              <th className="px-4 py-3">Sale · WBox</th><th className="px-4 py-3">Sale · NoBox</th>
              <th className="px-4 py-3">Non-Mov</th><th className="px-4 py-3">Defect</th>
              <th className="px-4 py-3">NoBox·PBT</th>
              {isAdmin && <th className="px-4 py-3">Actions</th>}
            </tr></thead>
            <tbody data-testid="grsr-tbody">
              {visible.map(r=>(
                <tr
                  key={r.id}
                  data-testid={`grsr-row-${r.id}`}
                  onClick={()=>openEdit(r)}
                  className={`border-b border-slate-100 hover:bg-orange-50/40 ${isAdmin ? "cursor-pointer" : ""}`}
                >
                  <td className="px-4 py-2 font-mono">{r.grsr_no}</td>
                  <td className="px-4 py-2 font-mono">{r.quickbill_no || "—"}</td>
                  <td className={`px-4 py-2 font-mono ${!(r.shopper_cn_no || "").trim() ? "text-rose-600 font-semibold" : ""}`}>{r.shopper_cn_no || "—"}</td>
                  <td className="px-4 py-2 font-mono">{fmtDMY(r.grsr_date)}</td>
                  <td className="px-4 py-2">{r.retailer_name}</td>
                  <td className="px-4 py-2">{r.company_name || "—"}</td>
                  <td className="px-4 py-2">{r.salesman_name}</td>
                  <td className={`px-4 py-2 font-mono ${r.document_quantity && r.actual_quantity_received !== r.document_quantity ? "text-rose-600 font-bold" : ""}`}>{r.actual_quantity_received}</td>
                  <td className="px-4 py-2 font-mono">{r.saleable_with_box || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.saleable_without_box || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.non_moving || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.defective || "—"}</td>
                  <td className="px-4 py-2 font-mono">{r.saleable_without_box_pb_tag_sticker || "—"}</td>
                  {isAdmin && (
                    <td className="px-4 py-2" onClick={(e)=>e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button onClick={()=>openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-sm" title="Edit"><PencilSimple size={14}/></button>
                        <button onClick={()=>remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm" title="Delete"><Trash size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {visible.length===0 && (
                <tr><td colSpan={isAdmin?14:13} className="px-4 py-10 text-center text-slate-500">
                  {q
                    ? `No matches for "${q}" in ${tab === "old" ? "Old GRSR" : "GRSR"}`
                    : (tab === "old" ? "No GRSR entries up to 31-07-2026" : "No GRSR entries from 01-08-2026 onward")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} GRSR</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
            {F("GRSR No", "grsr_no", "text", true)}
            {F("GRSR Date", "grsr_date", "date", true)}
            <div>
              <label className="label-cap">Retailer *</label>
              <Select value={form.retailer_id} onValueChange={(v)=>setForm({...form, retailer_id: v})}>
                <SelectTrigger data-testid="grsr-retailer-select" className="rounded-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{retailers.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Company</label>
              <Select value={form.company_id} onValueChange={(v)=>setForm({...form, company_id: v})}>
                <SelectTrigger data-testid="grsr-company-select" className="rounded-sm"><SelectValue placeholder="Select company"/></SelectTrigger>
                <SelectContent>{companies.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Salesman *</label>
              <Select value={form.salesman_name} onValueChange={(v)=>setForm({...form, salesman_name: v})}>
                <SelectTrigger data-testid="grsr-salesman-select" className="rounded-sm"><SelectValue placeholder="Select salesman"/></SelectTrigger>
                <SelectContent>{salesmen.map(s=><SelectItem key={s.mobile} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {F("Actual Quantity Received", "actual_quantity_received", "number", true)}
            {F("Mode of Transport", "mode_of_transport")}
            {F("Builty No", "builty_no")}
            {F("Total Shipper", "total_shipper", "number")}
            {F("Material Booked By", "material_booked_by")}
            {F("Retailer Doc No", "retailer_doc_no")}
            {F("Retailer Doc Date", "retailer_doc_date", "date")}
            {F("Document Quantity", "document_quantity", "number")}
            {F("Quickbill No", "quickbill_no")}
            {F("Quickbill CN Date", "quickbill_cn_date", "date")}
            {F("Shopper CN No", "shopper_cn_no")}
            {F("Shopper CN Date", "shopper_cn_date", "date")}

            <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
              <div className="label-cap text-orange-700">Category-wise Count</div>
              <div className="text-xs text-slate-500 mt-1">All optional. Leave blank if not applicable.</div>
            </div>
            {CATEGORY_FIELDS.map(([key, label]) => F(label, key, "number"))}

            <div className="col-span-2"><label className="label-cap">Remark (optional)</label><Textarea value={form.remark} onChange={(e)=>setForm({...form, remark: e.target.value})} rows={2}/></div>
            <div className="col-span-2">
              <label className="label-cap">Photo (optional)</label>
              <Input data-testid="grsr-photo-input" type="file" accept="image/*" onChange={onPhoto}/>
              {form.photo_data && <img src={form.photo_data} alt="preview" className="mt-2 max-h-32 rounded-sm border border-slate-200"/>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="grsr-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoView} onOpenChange={(v)=>!v && setPhotoView(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle>Photo</DialogTitle></DialogHeader>
          {photoView && <img src={photoView} alt="grsr" className="w-full rounded-sm border border-slate-200"/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
